import { InstitutoOpcao, normalizarStatus } from './instituto-previdenciario-opcoes';

/** Os 3 tipos dos indicadores e do gráfico por benefício (PRE-328). Os planos são os mesmos do Instituto (`INSTITUTO_PLANOS`). */
export const REQUERIMENTO_TIPOS_BENEFICIO: readonly InstitutoOpcao[] = [
  { id: 1, label: 'Aposentadoria' },
  { id: 2, label: 'Aposentadoria antecipada' },
  { id: 3, label: 'Aposentadoria por invalidez' },
];

/**
 * Tipo de `REQUERIMENTO_TIPOS_BENEFICIO` do benefício do catálogo (`ds_beneficio` da API): "Benefício por
 * Incapacidade" é a aposentadoria por invalidez, "Aposentadoria Antecipada" a antecipada e os demais contam como
 * "Aposentadoria" (PRE-328).
 */
export function getRequerimentoTipoBeneficio(beneficio: string | null | undefined): string {
  const nome = normalizarStatus(beneficio);
  if (nome.includes('incapacidade') || nome.includes('invalidez')) {
    return 'Aposentadoria por invalidez';
  }
  return nome.includes('antecipada') ? 'Aposentadoria antecipada' : 'Aposentadoria';
}

/** Rótulos do mock, na ordem do fluxo do requerimento. Com a API, os status vêm de `GET /v1/admin/requerimentos/status`. */
export const REQUERIMENTO_STATUS: readonly InstitutoOpcao[] = [
  { id: 1, label: 'Solicitado' },
  { id: 2, label: 'Documento rejeitado' },
  { id: 6, label: 'Documento reenviado' },
  { id: 3, label: 'Liberado para integração' },
  { id: 4, label: 'Concluído' },
  { id: 5, label: 'Cancelado' },
];

/** Status em que o administrador confere os documentos (aprova ou pede reenvio). */
export const REQUERIMENTO_STATUS_EM_ANALISE: readonly string[] = ['Solicitado', 'Documento reenviado'];

/** `statusAvaliacao` do documento que o participante reenviou e aguarda nova análise. */
export const DOCUMENTO_REENVIADO = 'Reenviado';

/** `statusAvaliacao` do documento rejeitado pelo administrador (o motivo vem em `avaliacoes`). */
export const DOCUMENTO_REJEITADO = 'Rejeitado';

/** Cor de um status: `cor` é a fatia do gráfico e o contorno da tag; `texto`, o texto da tag. */
export interface RequerimentoStatusCor {
  cor: string;
  texto: string;
}

/**
 * Cores dos status do requerimento, as mesmas no gráfico e nas tags. O design system não tem laranja
 * nem azul: esses dois vão em hex. Os rótulos da API (esteira da PRE-328) e os do mock usam as mesmas cores:
 * "Aguardando documentação" = "Documento rejeitado" e "Em processamento (Integração)" = "Liberado para integração".
 */
const STATUS_CORES: Record<string, RequerimentoStatusCor> = {
  solicitado: { cor: 'var(--color-yellow-v500)', texto: 'var(--color-yellow-v800, #8a6100)' },
  'aguardando documentacao': { cor: 'var(--color-red-v500)', texto: 'var(--color-red-v700)' },
  'documento rejeitado': { cor: 'var(--color-red-v500)', texto: 'var(--color-red-v700)' },
  'documento reenviado': { cor: 'var(--color-red-v300)', texto: 'var(--color-red-v600)' },
  'documento validado': { cor: 'var(--color-green-v200)', texto: 'var(--color-green-v800)' },
  'em processamento (integracao)': { cor: '#2f80c9', texto: '#1c5a94' },
  'liberado para integracao': { cor: '#2f80c9', texto: '#1c5a94' },
  concluido: { cor: 'var(--color-green-v500)', texto: 'var(--color-green-v800)' },
  cancelado: { cor: '#f0882a', texto: '#a3530b' },
};

/** Status fora da lista. */
const STATUS_COR_PADRAO: RequerimentoStatusCor = { cor: 'var(--color-gray-v500)', texto: 'var(--color-gray-v800)' };

/** Cor do status, sem diferenciar maiúsculas, acentos nem espaços nas pontas. */
export function getRequerimentoStatusCor(status: string | null | undefined): RequerimentoStatusCor {
  return STATUS_CORES[normalizarStatus(status)] ?? STATUS_COR_PADRAO;
}
